/**
 * The channel statistics panel, in the pisg tradition.
 *
 * One report comes back from the server as numbers and rankings; everything
 * here turns that into the six tabs behind the room menu. The server
 * deliberately sends no prose - it has two languages to choose between and no
 * business choosing - so the sentences under "Notable" are composed here, in
 * whichever language the reader is using.
 *
 * Bar heights are set through CSS custom properties rather than a `style`
 * attribute, and that is not a style preference: this app is served under a
 * Content-Security-Policy with no 'unsafe-inline' for styles, which blocks a
 * `style` attribute written by setAttribute. Writing to the CSSOM - which is
 * what `style.setProperty` does - is not restricted, so the bars get their
 * height that way and the policy stays as tight as it is.
 */
import { t, getLocale } from './i18n.js';

/** Local element builder, mirroring the one in main.js. */
function el(tag, props = {}, ...children) {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(props)) {
    if (key === 'class') node.className = value;
    else if (key === 'text') node.textContent = value;
    else if (key === 'vars') {
      // See the note above: CSSOM, never a style attribute.
      for (const [name, val] of Object.entries(value)) node.style.setProperty(name, val);
    } else if (key.startsWith('on') && typeof value === 'function') {
      node.addEventListener(key.slice(2).toLowerCase(), value);
    } else if (value !== null && value !== undefined && value !== false) {
      node.setAttribute(key, value === true ? '' : String(value));
    }
  }
  for (const child of children.flat()) {
    if (child === null || child === undefined || child === false) continue;
    node.append(child instanceof Node ? child : document.createTextNode(String(child)));
  }
  return node;
}

const number = (value) => Number(value ?? 0).toLocaleString(getLocale());
const decimal = (value, places = 1) =>
  Number(value ?? 0).toLocaleString(getLocale(), {
    minimumFractionDigits: places,
    maximumFractionDigits: places,
  });
const percent = (value) => `${decimal(value, 1)}%`;

/** "3h 24m" - the two largest units that apply, like the ban-expiry formatter. */
function duration(ms) {
  const total = Math.max(0, Math.floor(Number(ms ?? 0) / 1000));
  if (total === 0) return t('duration.s', { value: 0 });
  const parts = [];
  let rest = total;
  for (const [unit, secs] of [
    ['d', 86400],
    ['h', 3600],
    ['m', 60],
    ['s', 1],
  ]) {
    const value = Math.floor(rest / secs);
    if (value > 0) {
      parts.push(t(`duration.${unit}`, { value }));
      rest -= value * secs;
    }
    if (parts.length === 2) break;
  }
  return parts.join(' ');
}

const clockTime = (at) =>
  new Date(at).toLocaleTimeString(getLocale(), { hour: '2-digit', minute: '2-digit' });

const dateTime = (at) => new Date(at).toLocaleString(getLocale());

/**
 * "Today" / "Yesterday" / "N days ago", which is how the reference page reports
 * when somebody was last around. Counted in whole calendar days rather than in
 * 24-hour blocks, so something said late last night reads as yesterday.
 */
function lastSeen(at) {
  if (!at) return '—';
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const then = new Date(at);
  then.setHours(0, 0, 0, 0);
  const days = Math.round((startOfToday - then) / 86400000);
  if (days <= 0) return t('ui.stats.today');
  if (days === 1) return t('ui.stats.yesterday');
  return t('ui.stats.daysAgo', { count: days });
}

/**
 * A table with a header row, or a hint when there is nothing to put in it.
 *
 * Wrapped in its own scrolling box rather than relying on the section around it:
 * the talkers table carries eight columns and does not fit a phone, and a table
 * that overflows the dialog makes the whole page scroll sideways instead.
 */
function table(headers, rows, emptyText) {
  if (rows.length === 0) return el('p', { class: 'hint', text: emptyText });
  return el(
    'div',
    { class: 'stats-scroll' },
    el(
      'table',
      {},
      el('thead', {}, el('tr', {}, ...headers.map((h) => el('th', { text: h })))),
      el('tbody', {}, ...rows),
    ),
  );
}

const section = (title, ...body) =>
  el('section', { class: 'stats-section' }, el('h3', { text: title }), ...body);

// ------------------------------------------------------------------- activity

/**
 * The 24-hour clock, as a bar per hour coloured by quarter of the day.
 *
 * Heights are relative to the busiest hour rather than to the total, because a
 * channel's shape is what this is for: scaled against the total, a channel
 * spread evenly over the day would be twenty-four bars all too short to read.
 */
function hourChart(hours) {
  const total = hours.reduce((sum, n) => sum + n, 0);
  const peak = Math.max(...hours, 1);
  const bars = hours.map((lines, hour) =>
    el(
      'div',
      { class: `stats-hour q${Math.floor(hour / 6)}`, title: t('ui.stats.hourTitle', { hour, count: lines }) },
      el('span', { class: 'stats-hour-value', text: total > 0 ? percent((lines / total) * 100) : '' }),
      el('div', { class: 'stats-hour-bar', vars: { '--h': `${(lines / peak) * 100}%` } }),
      el('span', { class: 'stats-hour-label', text: String(hour) }),
    ),
  );
  return el(
    'div',
    {},
    el('div', { class: 'stats-hours' }, ...bars),
    el(
      'div',
      { class: 'stats-legend' },
      ...[0, 1, 2, 3].map((q) =>
        el(
          'span',
          { class: 'stats-legend-item' },
          el('i', { class: `stats-swatch q${q}` }),
          `${q * 6}–${q * 6 + 5}`,
        ),
      ),
    ),
  );
}

/** `2026-08-17` → a short date in the reader's language ("17 Aug", "17.8."). */
function shortDate(day) {
  const [y, m, d] = String(day).split('-').map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1).toLocaleDateString(getLocale(), {
    day: 'numeric',
    month: 'short',
  });
}

/**
 * Rounds an axis maximum up to something a person would choose: 1, 2 or 5 times
 * a power of ten. An axis topping out at "133" is a number nobody picked.
 */
function niceCeiling(value) {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  // Finer steps than the usual 1/2/5, because the coarse set wastes the plot: a
  // peak of 133 would round to 200 and leave a third of the height empty, where
  // 150 is just as round a number to read off an axis.
  for (const step of [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10]) {
    const candidate = step * magnitude;
    // Halves only where they land on a whole number, so a small channel's axis
    // does not read "1.5 lines".
    if (candidate >= value && Number.isInteger(candidate / 2)) return candidate;
  }
  return 10 * magnitude;
}

/**
 * Lines per day for the last month.
 *
 * This began as a bare strip of bars, which was the weakest thing on the panel:
 * the only way to read a day or a value was to hover one, which is no use on a
 * phone and no use to a screen reader. It now has both axes, so the dates and the
 * scale are readable without touching anything.
 *
 * Three deliberate choices:
 *
 *   - **One label, not thirty.** A number over every bar is unreadable at this
 *     width, so only the busiest day is labelled; the y-axis carries the rest.
 *   - **A tick every seventh day**, plus the last, because thirty dates will not
 *     fit and a weekly rhythm is what anybody reads a month by.
 *   - **A table underneath**, collapsed. A tooltip must never be the only way to
 *     reach a value, and a native <details> costs no space until it is opened and
 *     works from the keyboard without any script.
 */
function trendChart(trend) {
  if (!trend || trend.length === 0) return null;

  const values = trend.map((d) => d.lines);
  const total = values.reduce((sum, n) => sum + n, 0);
  if (total === 0) return null;

  const peak = Math.max(...values);
  const busiest = trend[values.indexOf(peak)];
  const top = niceCeiling(peak);
  const quiet = values.filter((n) => n === 0).length;
  // Averaged over the days, silent ones included - that is what "a day" means
  // here, and dropping the quiet days would overstate a sporadic channel.
  const average = total / trend.length;

  // Every seventh day, and always the last, so the axis ends on today.
  const isTick = (index) => index === trend.length - 1 || (trend.length - 1 - index) % 7 === 0;

  const bars = trend.map((day, index) =>
    el(
      'div',
      { class: 'stats-day' },
      el(
        'div',
        {
          class: `stats-day-bar${day === busiest ? ' peak' : ''}`,
          vars: { '--h': `${(day.lines / top) * 100}%` },
          title: t('ui.stats.trendTitle', { day: shortDate(day.day), count: number(day.lines) }),
        },
        // The value rides on the bar's cap, so it stays put whatever the scale
        // was rounded up to.
        day === busiest ? el('span', { class: 'stats-day-value', text: number(day.lines) }) : null,
      ),
      isTick(index) ? el('span', { class: 'stats-day-tick', text: shortDate(day.day) }) : null,
    ),
  );

  return section(
    t('ui.stats.trend'),
    el(
      'div',
      { class: 'stats-chart' },
      // The scale, as three hairlines behind the bars with their values in the
      // gutter. Solid and one shade off the surface, so they stay behind the data.
      el(
        'div',
        { class: 'stats-chart-scale' },
        ...[top, top / 2, 0].map((value) =>
          el('div', { class: 'stats-chart-rule' }, el('span', { text: number(value) })),
        ),
      ),
      el(
        'div',
        {
          class: 'stats-days',
          role: 'img',
          'aria-label': t('ui.stats.trendAria', {
            days: number(trend.length),
            lines: number(total),
            peak: number(peak),
            day: shortDate(busiest.day),
          }),
        },
        ...bars,
      ),
    ),
    el('p', {
      class: 'hint',
      text:
        `${t('ui.stats.trendBusiest', { day: shortDate(busiest.day), count: number(peak) })} · ` +
        `${t('ui.stats.trendAverage', { count: decimal(average, 1) })}` +
        (quiet > 0 ? ` · ${t('ui.stats.trendQuiet', { count: quiet })}` : ''),
    }),
    el(
      'details',
      { class: 'stats-details' },
      el('summary', { text: t('ui.stats.showTable') }),
      table(
        [t('ui.stats.day'), t('ui.stats.lines')],
        trend.map((day) =>
          el(
            'tr',
            {},
            el('td', { text: shortDate(day.day) }),
            el('td', { text: number(day.lines) }),
          ),
        ),
        t('ui.stats.nothingYet'),
      ),
    ),
  );
}

function activityTab(report) {
  const quarters = el(
    'div',
    { class: 'stats-quarters' },
    ...report.quarters.map((entries, index) =>
      el(
        'div',
        { class: 'stats-quarter' },
        el(
          'h4',
          { class: `q${index}` },
          el('i', { class: 'stats-swatch' }),
          `${index * 6}–${index * 6 + 5}`,
        ),
        entries.length === 0
          ? el('p', { class: 'hint', text: '—' })
          : el(
              'ol',
              { class: 'stats-rank' },
              ...entries.map((entry) =>
                el(
                  'li',
                  {},
                  el('span', { class: 'stats-rank-name', text: entry.nickname }),
                  el('span', { class: 'stats-rank-value', text: number(entry.lines) }),
                ),
              ),
            ),
      ),
    ),
  );

  return el(
    'div',
    {},
    section(t('ui.stats.byHour'), hourChart(report.hours)),
    section(t('ui.stats.byQuarter'), quarters),
    trendChart(report.trend),
  );
}

// -------------------------------------------------------------------- talkers

function talkersTab(report) {
  const rows = report.talkers.map((talker, index) =>
    el(
      'tr',
      {},
      el('td', { class: 'stats-rank-cell', text: String(index + 1) }),
      el('td', { class: 'stats-nick', text: talker.nickname }),
      el('td', { text: number(talker.lines) }),
      el(
        'td',
        {},
        talker.peakHour === null
          ? '—'
          : el(
              'span',
              { class: `stats-peak q${Math.floor(talker.peakHour / 6)}` },
              t('ui.stats.peakHour', { hour: talker.peakHour }),
            ),
      ),
      el('td', { text: number(talker.words) }),
      el('td', { text: decimal(talker.wordsPerLine) }),
      el('td', { text: decimal(talker.charsPerLine) }),
      el('td', { text: lastSeen(talker.lastSeen) }),
    ),
  );

  const alsoRan =
    report.alsoRan.length > 0
      ? el(
          'p',
          { class: 'hint stats-alsoran' },
          `${t('ui.stats.alsoRan')} `,
          ...report.alsoRan.map((entry, index) =>
            el('span', {}, index > 0 ? ', ' : '', `${entry.nickname} (${number(entry.lines)})`),
          ),
        )
      : null;

  return el(
    'div',
    {},
    table(
      [
        '#',
        t('ui.stats.who'),
        t('ui.stats.lines'),
        t('ui.stats.when'),
        t('ui.stats.words'),
        t('ui.stats.wordsPerLine'),
        t('ui.stats.charsPerLine'),
        t('ui.stats.lastSeen'),
      ],
      rows,
      t('ui.stats.nothingYet'),
    ),
    alsoRan,
    report.omitted > 0
      ? el('p', { class: 'hint', text: t('ui.stats.omitted', { count: report.omitted }) })
      : null,
  );
}

// ---------------------------------------------------------------------- words

function wordsTab(report) {
  const wordRows = report.words.map((entry, index) =>
    el(
      'tr',
      {},
      el('td', { class: 'stats-rank-cell', text: String(index + 1) }),
      el('td', { class: 'stats-word', text: entry.word }),
      el('td', { text: number(entry.uses) }),
      el('td', { class: 'stats-nick', text: entry.lastBy ?? '—' }),
    ),
  );

  const mentionRows = report.mentions.map((entry, index) =>
    el(
      'tr',
      {},
      el('td', { class: 'stats-rank-cell', text: String(index + 1) }),
      el('td', { class: 'stats-nick', text: entry.nickname }),
      el('td', { text: number(entry.mentions) }),
      el('td', { class: 'stats-nick', text: entry.lastBy ?? '—' }),
    ),
  );

  /*
   * Addresses are shown as text, never as links. A statistics page is a list of
   * things strangers pasted into a chat room, and turning that into a row of
   * one-click destinations is a different feature with a different risk.
   */
  const linkRows = report.links.map((entry, index) =>
    el(
      'tr',
      {},
      el('td', { class: 'stats-rank-cell', text: String(index + 1) }),
      el('td', { class: 'stats-link', text: entry.url }),
      el('td', { text: number(entry.uses) }),
      el('td', { class: 'stats-nick', text: entry.lastBy ?? '—' }),
    ),
  );

  return el(
    'div',
    {},
    section(
      t('ui.stats.topWords'),
      table(
        ['#', t('ui.stats.word'), t('ui.stats.uses'), t('ui.stats.lastBy')],
        wordRows,
        t('ui.stats.nothingYet'),
      ),
    ),
    section(
      t('ui.stats.mostReferenced'),
      table(
        ['#', t('ui.stats.who'), t('ui.stats.mentions'), t('ui.stats.lastBy')],
        mentionRows,
        t('ui.stats.nothingYet'),
      ),
    ),
    report.quotesEnabled
      ? section(
          t('ui.stats.hotLinks'),
          table(
            ['#', t('ui.stats.link'), t('ui.stats.uses'), t('ui.stats.lastBy')],
            linkRows,
            t('ui.stats.nothingYet'),
          ),
        )
      : null,
  );
}

// ---------------------------------------------------------------------- on air

function mediaTab(report) {
  const media = report.media;
  const leaderRows = media.leaders.map((entry, index) =>
    el(
      'tr',
      {},
      el('td', { class: 'stats-rank-cell', text: String(index + 1) }),
      el('td', { class: 'stats-nick', text: entry.nickname }),
      el('td', { text: duration(entry.totalMs) }),
      el('td', { text: duration(entry.audioMs) }),
      el('td', { text: duration(entry.videoMs) }),
      el('td', { text: duration(entry.screenMs) }),
      el('td', { text: number(entry.sessions) }),
    ),
  );

  const recentRows = media.recent.map((entry) =>
    el(
      'tr',
      {},
      el('td', { class: 'stats-nick', text: entry.nickname }),
      el('td', {}, el('span', { class: `stats-source ${entry.source}`, text: t(`ui.stats.source.${entry.source}`) })),
      el('td', { text: dateTime(entry.startedAt) }),
      el(
        'td',
        {},
        entry.live
          ? el('span', { class: 'stats-live', text: t('ui.stats.live') })
          : duration(entry.durationMs),
      ),
    ),
  );

  return el(
    'div',
    {},
    el(
      'div',
      { class: 'stats-figures' },
      figure(duration(media.totalMs), t('ui.stats.totalAirtime')),
      figure(number(media.sessions), t('ui.stats.broadcasts')),
      figure(number(media.broadcasters), t('ui.stats.broadcasters')),
    ),
    section(
      t('ui.stats.airtimeLeaders'),
      table(
        [
          '#',
          t('ui.stats.who'),
          t('ui.stats.total'),
          t('ui.stats.source.mic'),
          t('ui.stats.source.camera'),
          t('ui.stats.source.screen'),
          t('ui.stats.sessions'),
        ],
        leaderRows,
        t('ui.stats.noBroadcasts'),
      ),
    ),
    section(
      t('ui.stats.recentBroadcasts'),
      table(
        [t('ui.stats.who'), t('ui.stats.what'), t('ui.stats.started'), t('ui.stats.duration')],
        recentRows,
        t('ui.stats.noBroadcasts'),
      ),
    ),
  );
}

const figure = (value, label) =>
  el(
    'div',
    { class: 'stats-figure' },
    el('strong', { text: value }),
    el('span', { text: label }),
  );

// --------------------------------------------------------------------- jukebox

/**
 * A track's name, or an honest admission that it is not known.
 *
 * The server stores null rather than the video id when a name could not be
 * resolved, so an id here means "we never found out" - and showing a bare
 * `dQw4w9WgXcQ` as though it were a title tells the reader nothing. It is shown
 * as an id, marked as one, with the reason in its tooltip; the server retries
 * the lookup in the background, so the name usually appears next time.
 */
function trackName(entry) {
  // The cell truncates a long name, so the whole of it goes in the tooltip.
  if (entry.title) return el('span', { text: entry.title, title: entry.title });
  return el('span', {
    class: 'stats-unnamed',
    text: t('ui.stats.unnamedTrack'),
    title: t('ui.stats.unnamedTrackHint', { videoId: entry.videoId }),
  });
}

function jukeboxTab(report) {
  const jukebox = report.jukebox;
  const leaderRows = jukebox.leaders.map((entry, index) =>
    el(
      'tr',
      {},
      el('td', { class: 'stats-rank-cell', text: String(index + 1) }),
      el('td', { class: 'stats-nick', text: entry.nickname }),
      el('td', { text: number(entry.tracks) }),
      el('td', { text: entry.totalMs > 0 ? duration(entry.totalMs) : '—' }),
    ),
  );

  const topRows = jukebox.top.map((entry, index) =>
    el(
      'tr',
      {},
      el('td', { class: 'stats-rank-cell', text: String(index + 1) }),
      el('td', { class: 'stats-track' }, trackName(entry)),
      el('td', { text: entry.author ?? '—' }),
      el('td', { text: number(entry.plays) }),
    ),
  );

  const recentRows = jukebox.recent.map((entry) =>
    el(
      'tr',
      {},
      el('td', { class: 'stats-nick', text: entry.nickname }),
      el('td', { class: 'stats-track' }, trackName(entry)),
      el('td', { text: entry.durationMs ? duration(entry.durationMs) : '—' }),
      el('td', { text: dateTime(entry.addedAt) }),
    ),
  );

  return el(
    'div',
    {},
    el(
      'div',
      { class: 'stats-figures' },
      figure(number(jukebox.tracks), t('ui.stats.tracksQueued')),
      figure(number(jukebox.uniqueTracks), t('ui.stats.uniqueTracks')),
      figure(jukebox.totalMs > 0 ? duration(jukebox.totalMs) : '—', t('ui.stats.totalMusic')),
    ),
    section(
      t('ui.stats.jukeboxLeaders'),
      table(
        ['#', t('ui.stats.who'), t('ui.stats.tracks'), t('ui.stats.runningTime')],
        leaderRows,
        t('ui.stats.noTracks'),
      ),
    ),
    section(
      t('ui.stats.topTracks'),
      table(
        ['#', t('ui.stats.track'), t('ui.stats.artist'), t('ui.stats.timesQueued')],
        topRows,
        t('ui.stats.noTracks'),
      ),
    ),
    section(
      t('ui.stats.recentTracks'),
      table(
        [t('ui.stats.who'), t('ui.stats.track'), t('ui.stats.length'), t('ui.stats.added')],
        recentRows,
        t('ui.stats.noTracks'),
      ),
    ),
  );
}

// --------------------------------------------------------------------- notable

/**
 * The "big numbers" and "other interesting" sentences.
 *
 * `stat` names the entry in the report's `highlights`; `kind` decides whether
 * the sentence is given a percentage or a plain count, and `quote` whether an
 * example line goes underneath it. The runner-up gets its own phrasing, which is
 * what makes the section read like prose rather than like a table with the
 * borders taken off - so each stat has two keys, `<stat>` and `<stat>2`.
 */
const NOTABLE = [
  { stat: 'questions', kind: 'percent' },
  { stat: 'yells', kind: 'percent' },
  { stat: 'caps', kind: 'percent', quote: true },
  { stat: 'smiles', kind: 'percent' },
  { stat: 'frowns', kind: 'percent' },
  { stat: 'swears', kind: 'percent' },
  { stat: 'slapsGiven', kind: 'count', quote: true },
  { stat: 'slapsTaken', kind: 'count' },
  { stat: 'actions', kind: 'count', quote: true },
  { stat: 'monologues', kind: 'count' },
  { stat: 'kicksTaken', kind: 'count' },
  { stat: 'kicksGiven', kind: 'count' },
  { stat: 'opsGiven', kind: 'count' },
  { stat: 'deopsGiven', kind: 'count' },
  { stat: 'joins', kind: 'count' },
  { stat: 'uploads', kind: 'count' },
  { stat: 'mostWords', kind: 'count' },
  { stat: 'longestLines', kind: 'decimal' },
  { stat: 'shortestLines', kind: 'decimal' },
  { stat: 'mostWordsPerLine', kind: 'decimal', places: 2 },
];

function notableTab(report) {
  const entries = [];

  for (const { stat, kind, quote, places = 1 } of NOTABLE) {
    const ranking = report.highlights?.[stat] ?? [];
    if (ranking.length === 0) continue;

    ranking.slice(0, 2).forEach((entry, index) => {
      const params = {
        nickname: entry.nickname,
        count: entry.value,
        value: kind === 'decimal' ? decimal(entry.value, places) : number(entry.value),
        percent: percent(entry.percent ?? 0),
        lines: number(entry.lines ?? 0),
        longest: number(entry.longest ?? 0),
      };
      const body = [
        el('p', { class: index === 0 ? 'stats-fact' : 'stats-fact second' , text: t(`ui.stats.fun.${stat}${index === 1 ? '2' : ''}`, params) }),
      ];
      // Only the leader is quoted, which is what the reference page does: one
      // example, under the person it is an example of.
      if (quote && index === 0 && report.quotesEnabled && entry.example) {
        body.push(
          el(
            'blockquote',
            { class: 'stats-quote' },
            el('span', { class: 'stats-nick', text: entry.nickname }),
            ' ',
            el('span', { text: entry.example }),
          ),
        );
      }
      entries.push(...body);
    });
  }

  const averages = section(
    t('ui.stats.channelAverages'),
    el(
      'div',
      { class: 'stats-figures' },
      figure(decimal(report.totals.charsPerLine), t('ui.stats.charsPerLine')),
      figure(decimal(report.totals.wordsPerLine, 2), t('ui.stats.wordsPerLine')),
      figure(number(report.totals.topicChanges), t('ui.stats.topicChanges')),
    ),
  );

  const topics =
    report.topics.length > 0
      ? section(
          t('ui.stats.recentTopics'),
          el(
            'ul',
            { class: 'stats-topics' },
            ...report.topics.map((entry) =>
              el(
                'li',
                {},
                el('span', { class: 'stats-topic', text: entry.topic }),
                el('span', {
                  class: 'hint',
                  text: t('ui.stats.topicBy', {
                    nickname: entry.setBy ?? '—',
                    when: lastSeen(entry.at),
                    time: clockTime(entry.at),
                  }),
                }),
              ),
            ),
          ),
        )
      : null;

  return el(
    'div',
    {},
    entries.length > 0
      ? section(t('ui.stats.notable'), ...entries)
      : el('p', { class: 'hint', text: t('ui.stats.nothingNotable') }),
    averages,
    topics,
  );
}

// ----------------------------------------------------------------------- entry

const TABS = {
  activity: activityTab,
  talkers: talkersTab,
  words: wordsTab,
  media: mediaTab,
  jukebox: jukeboxTab,
  fun: notableTab,
};

export const STATS_TABS = Object.keys(TABS);

/** One line describing the period and size of the sample, under the title. */
export function statsSummary(report) {
  if (!report?.enabled) return t('ui.stats.disabled');
  if (report.totals.lines === 0) return t('ui.stats.empty');
  return t('ui.stats.summary', {
    room: report.room,
    days: number(report.period.days),
    nicknames: number(report.totals.nicknames),
    lines: number(report.totals.lines),
  });
}

/**
 * Renders one tab of a report into `host`.
 *
 * A report with nothing in it renders as a note rather than as six empty
 * tables: a brand-new channel has no statistics, and saying so is more use than
 * showing the shape of the ones it will have.
 */
export function renderStats(host, report, tab = 'activity') {
  if (!report?.enabled) {
    host.replaceChildren(el('p', { class: 'hint', text: t('ui.stats.disabled') }));
    return;
  }
  if (report.totals.lines === 0 && report.media.sessions === 0 && report.jukebox.tracks === 0) {
    host.replaceChildren(el('p', { class: 'hint', text: t('ui.stats.empty') }));
    return;
  }
  const render = TABS[tab] ?? activityTab;
  host.replaceChildren(render(report));
}

export default { renderStats, statsSummary, STATS_TABS };
